import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useBaseUrl } from './BaseUrlContext';

const CompaniesContext = createContext();

export const CompaniesProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { baseUrl, loading: baseUrlLoading } = useBaseUrl();

  const fetchCompanies = async () => {
    if (!baseUrl) return;
    
    try {
      setLoading(true);
      setError(null);
      // Use the correct API endpoint structure
      const response = await fetch(`${baseUrl}Companies`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCompanies(data);
      
      // Auto-select the first company if none is selected
      if (data.length > 0 && !selectedCompany) {
        setSelectedCompany(data[0]);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching companies:', err);
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!baseUrlLoading && baseUrl) {
      fetchCompanies();
    }
  }, [baseUrl, baseUrlLoading]);

  const handleCompanyClick = useCallback((company) => {
    setSelectedCompany(company);
  }, []);

  const value = useMemo(() => ({
    companies,
    loading: loading || baseUrlLoading,
    error,
    selectedCompany,
    handleCompanyClick,
    refetch: fetchCompanies
  }), [companies, loading, baseUrlLoading, error, selectedCompany, handleCompanyClick]);

  return (
    <CompaniesContext.Provider value={value}>
      {children}
    </CompaniesContext.Provider>
  );
};

export const useCompanies = () => {
  const context = useContext(CompaniesContext);
  if (context === undefined) {
    throw new Error('useCompanies must be used within a CompaniesProvider');
  }
  return context;
};
